import { RefreshRounded } from '@mui/icons-material';
import { Typography } from '@mui/material';
import cx from 'classnames';
import { fetchDashboardMetrics, setContextualSupport, unsetContextualSupport } from 'js/actions';
import DataFlowDetailActionsContainer from 'js/components/data_flows/DataFlowDetailActionsContainer';
import FlowDetailViewContainer from 'js/components/data_flows/cards/FlowDetailViewContainer';
import NoContentTextView from 'js/components/elements/NoContentTextView';
import {
  GET_DASHBOARD_METRICS_REQUEST,
  GET_PIPELINES_REQUEST,
  GET_VENDORS_REQUEST,
} from 'js/constants/actionTypes';
import { ROUTE_REDESIGN } from 'js/constants/pathnames';
import { SUPPORT_KEY_DASHBOARD } from 'js/constants/supportPageKeys';
import DateUtils from 'js/lib/DateUtils';
import { filter, isEmpty, isString, orderBy } from 'lodash';
import { columns } from 'new/components/FlowsTable/columns';
import { FlowsKnowledgePanelContent } from 'new/components/dashboard/Dashboard';
import { PreviewFlowLeaf } from 'new/components/monitor/DataFlowStatus/PreviewFlowLeaf';
import { AclSelection } from 'new/elements/AclSelection';
import { IconButton } from 'new/elements/IconButton';
import { KnowledgePanel } from 'new/elements/KnowledgePanel';
import { PillTabs } from 'new/elements/PillTabs';
import { Stack } from 'new/elements/Stack';
import { Table } from 'new/elements/Table';
import { useFlatFlows } from 'new/hooks/adapters/useFlatFlows';
import { useIsLoading } from 'new/hooks/adapters/useIsLoading';
import { ScreenComponent, ScreenConfig } from 'new/routes/RouterManager';
import React, { ReactNode, RefObject, useCallback, useEffect, useRef, useState } from 'react';
import useAppDispatch from 'js/hooks/useAppDispatch';
import { useDataFlowStatusSelector } from 'new/hooks/useDataFlowStatusSelector';

import styles from './DataFlowStatus.module.scss';

interface DataFlowStatusProps {
  parentRef?: RefObject<HTMLDivElement>;
  title?: string | ReactNode;
  hideRefresh?: boolean;
  rootClassName?: string;
  tableContainerClassname?: string;
  topClassName?: string;
  disableFullHeight?: boolean;
}

const FILTER_TYPE_ALERT = 'alert';
const COLOR_SEVERITY = { OK: '0', WARNING: '1', ERROR: '2' };
const flowFilters = [
  {
    label: 'Flows with Destinations',
    value: 'toSink',
  },
  {
    label: 'Flows with warnings',
    value: COLOR_SEVERITY.WARNING,
    type: FILTER_TYPE_ALERT,
  },
  {
    label: 'Flows with errors',
    value: COLOR_SEVERITY.ERROR,
    type: FILTER_TYPE_ALERT,
  },
  {
    label: 'Without errors or warnings',
    value: COLOR_SEVERITY.OK,
    type: FILTER_TYPE_ALERT,
  },
];

const filterAndSort = (flows, filters) => {
  let preparedFlows = [...flows];
  // filter flows
  const severityFilterValues = filter(filters, (v) => !isNaN(v)).map((v) => Number(v));
  const hasToSinkFilter = filters.indexOf('toSink') > -1;

  if (!isEmpty(filters)) {
    preparedFlows = filter(
      preparedFlows,
      ({ severity, isLeafSink }) =>
        (isEmpty(severityFilterValues) || severityFilterValues.indexOf(severity) > -1) &&
        (!hasToSinkFilter || (hasToSinkFilter && isLeafSink)),
    );
  }

  // sort flows
  preparedFlows = orderBy(
    preparedFlows,
    [
      (flow) => {
        let value = flow.severity;

        if (isString(value)) {
          value = value.toLowerCase();
        }

        return value === null ? '' : value;
      },
      'sourceRecordCount',
      ({ rootName }) => rootName && rootName.toLowerCase(),
    ],
    ['desc', 'desc', 'asc'],
  );
  return preparedFlows;
};

const Title = ({ title }: { title: string | ReactNode }) => {
  if (typeof title !== 'string' && React.isValidElement(title)) {
    return title;
  }
  return <Typography variant="subtitle1">{title || 'Data Flow Status'}</Typography>;
};

export const DataFlowStatus: ScreenComponent<DataFlowStatusProps> = (
  props: DataFlowStatusProps,
) => {
  const dispatch = useAppDispatch();
  const tableRef = useRef();
  const requested = useRef(false);

  const { aclQueryMode, selectedCardID, dashboard } = useDataFlowStatusSelector();

  const loading = useIsLoading(
    GET_PIPELINES_REQUEST,
    GET_VENDORS_REQUEST,
    GET_DASHBOARD_METRICS_REQUEST,
  );
  const [filters, setFilters] = useState([flowFilters[0].value]);
  const flows = useFlatFlows();

  useEffect(() => {
    if (!requested.current) {
      onRefresh();
      requested.current = true;
    }
    dispatch(setContextualSupport(SUPPORT_KEY_DASHBOARD));

    return function cleanup() {
      dispatch(unsetContextualSupport(SUPPORT_KEY_DASHBOARD));
    };
  }, []);

  const onFilterChange = (value) => {
    setFilters(value);
  };

  const onRefresh = () => {
    dispatch(fetchDashboardMetrics(true, aclQueryMode));
  };

  const onModeChange = (mode) => {
    dispatch(fetchDashboardMetrics(true, mode));
  };

  const getRowClassName = (row) => {
    return styles['severity' + row?.severity];
  };

  const rowEndRender = useCallback(
    (row) => (row ? <DataFlowDetailActionsContainer data={row.root} insightsOnly /> : null),
    [],
  );

  const pipelineStartTime = dashboard.start_time;
  const pipelineEndTime = dashboard.end_time;

  const displayPipelineStartTime = pipelineStartTime
    ? DateUtils.convertDateStringToFormat(pipelineStartTime, 'MMM D, YYYY (h:mma z)')
    : null;
  const displayPipelineEndTime = pipelineStartTime
    ? DateUtils.convertDateStringToFormat(pipelineEndTime, 'MMM D, YYYY (h:mma z)')
    : null;

  const filteredFlows = filterAndSort(flows, filters);
  const noFlows = requested.current && filteredFlows?.length === 0 && loading === false;

  return (
    <Stack height={props.disableFullHeight ? undefined : '100%'} className={props.rootClassName}>
      <div className={styles.container}>
        <div className={props.topClassName}>
          <div className={styles.subheader}>
            <KnowledgePanel alwaysShowUnderline knowledgeContent={<FlowsKnowledgePanelContent />}>
              <Title title={props.title} />
            </KnowledgePanel>
            {!props.hideRefresh ? (
              <>
                <IconButton onClick={onRefresh}>
                  <RefreshRounded />
                </IconButton>
                <Typography variant="body3">
                  {displayPipelineStartTime} - {displayPipelineEndTime}
                </Typography>
              </>
            ) : null}
          </div>
          <div className={styles.filterSection}>
            <PillTabs
              multiple
              tabs={flowFilters}
              onSelectionChange={onFilterChange}
              selected={filters}
            />
            <AclSelection onChange={onModeChange} />
          </div>
        </div>
        <div
          ref={tableRef}
          className={cx(
            filteredFlows.length ? styles.tableContainer : styles.tableContainerNoContent,
            props.tableContainerClassname,
          )}
        >
          <Table
            initialOpenData={filteredFlows?.[0]}
            shouldRowExpand={(open, rowData) => open?.leaf?.id === rowData?.leaf?.id}
            skeletonModeLength={20}
            loading={loading}
            parentScrollRef={props.parentRef || tableRef}
            rowCollapsibleRender={(row) => (
              <PreviewFlowLeaf {...row} isDataFlowStatus height={400} />
            )}
            collapseOnRowClick
            getRowClassName={getRowClassName}
            getRowTdClassName={() => styles.rowTd}
            columns={columns}
            rows={filteredFlows}
            rowEndRender={rowEndRender}
          />
        </div>

        {noFlows ? (
          <NoContentTextView
            rootStyle={style.no_context_text_view.root}
            title="No Data Flows active in the past day match your chosen filters"
          />
        ) : null}
      </div>
      {selectedCardID && <FlowDetailViewContainer insightsOnly />}
    </Stack>
  );
};

const style = {
  no_context_text_view: {
    root: {
      padding: '16px',
      height: '120px',
      alignItems: 'center',
      justifyContent: 'center',
      borderBottom: '1px solid #E4E4E7',
    },
  },
};

DataFlowStatus.screenConfig = {
  path: `${ROUTE_REDESIGN}/monitor/daily-per-flow-stats`,
  domain: 'monitor',
  header: {
    title: 'Data Flows',
    slugTitle: 'Data Flows',
  },
  screenName: 'DataFlowStatus',
} as ScreenConfig<DataFlowStatusProps>;
